import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { AiOutlineRedo } from "react-icons/ai";
import { HiX } from "react-icons/hi";
import { Button, Tooltip } from "@/components/ui";
import AIBadge from "@/components/ui/ai-badge";
import { formatToHumanReadable } from "@/lib/format-data";
import { parseError } from "@/lib/parse-error";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { ToolCallSummary } from "@/types/call";
import { SuggestedMessage } from "@/types/chat";
import RegenerateAITextModal from "./regenerate-ai-text-modal";
import { useDismissSuggestedMessageMutation, useExecuteToolCallMutation, useSendSuggestedMessageMutation } from "@/query/comms/chat";
type SuggestedContentProps = {
  chatId: string;
  suggestion?: SuggestedMessage | ToolCallSummary;
  setSuggestion?: (suggestion: SuggestedMessage | ToolCallSummary | undefined) => void;
  onSendMessage?: (messageContent: string, selectedFiles: File[]) => void;
};
export default function SuggestedContent(t0) {
  const $ = _c(59);
  const {
    chatId,
    suggestion,
    setSuggestion,
    onSendMessage
  } = t0;
  const {
    toast
  } = useToast();
  const [isAnimating, setIsAnimating] = useState(false);
  const {
    mutateAsync: executeToolCall
  } = useExecuteToolCallMutation();
  const {
    mutateAsync: sendSuggestedMessage
  } = useSendSuggestedMessageMutation();
  const {
    mutateAsync: dismissSuggestion
  } = useDismissSuggestedMessageMutation();
  if (!suggestion || !setSuggestion || !onSendMessage) {
    return null;
  }
  const isTool = "toolName" in suggestion;
  let t1;
  if ($[0] !== chatId || $[1] !== executeToolCall || $[2] !== isTool || $[3] !== onSendMessage || $[4] !== sendSuggestedMessage || $[5] !== setSuggestion || $[6] !== suggestion.content || $[7] !== suggestion.id || $[8] !== toast) {
    t1 = async content => {
      setIsAnimating(true);
      if (isTool) {
        ;
        try {
          const response = await executeToolCall({
            toolCallId: content.id,
            chatId
          });
          if (response) {
            toast({
              variant: "success",
              title: `${formatToHumanReadable((content as ToolCallSummary).toolName)} executed!`,
              description: (content as ToolCallSummary).toolCallDescription
            });
            setTimeout(() => {
              setIsAnimating(false);
              setSuggestion(response);
            }, 500);
            return;
          }
        } catch (t2) {
          const error = t2;
          setIsAnimating(false);
          toast({
            variant: "error",
            title: `${formatToHumanReadable((content as ToolCallSummary).toolName)} failed!`,
            description: await parseError(error)
          });
        }
      } else {
        await sendSuggestedMessage({
          suggestedMessageId: suggestion.id,
          chatId
        });
        onSendMessage(suggestion.content, []);
      }
      setIsAnimating(false);
    };
    $[0] = chatId;
    $[1] = executeToolCall;
    $[2] = isTool;
    $[3] = onSendMessage;
    $[4] = sendSuggestedMessage;
    $[5] = setSuggestion;
    $[6] = suggestion.content;
    $[7] = suggestion.id;
    $[8] = toast;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const onAction = t1;
  const isDisabled = isTool ? (suggestion as ToolCallSummary).hasBeenCalled : (suggestion as SuggestedMessage).isSelected;
  let t2;
  if ($[10] !== isAnimating) {
    t2 = isAnimating ? {
      x: "100%",
      opacity: 0
    } : {
      x: 0,
      opacity: 1
    };
    $[10] = isAnimating;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      duration: 0.5
    };
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <AIBadge className="h-3.5 w-3.5 shrink-0" />;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== isTool || $[15] !== suggestion) {
    t5 = isTool ? formatToHumanReadable((suggestion as ToolCallSummary).toolName) : "Suggested Response";
    $[14] = isTool;
    $[15] = suggestion;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t5) {
    t6 = <div className="flex min-w-0 items-center gap-1">{t4}<span className="truncate text-body-emphasis text-accent-foreground">{t5}</span></div>;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const t7 = isDisabled ? "Cannot regenerate an executed action" : "Ask AI to regenerate this action or message";
  const t8 = isDisabled ? "cursor-not-allowed text-accent-foreground/50" : "text-accent-foreground hover:bg-accent-foreground/10";
  let t9;
  if ($[19] !== t8) {
    t9 = cx("h-6 w-6 shrink-0 rounded-md p-1", t8);
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <AiOutlineRedo className={t9} />;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t7) {
    t11 = <Tooltip content={t7}>{t10}</Tooltip>;
    $[23] = t10;
    $[24] = t7;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== setSuggestion || $[27] !== suggestion || $[28] !== t11) {
    t12 = <RegenerateAITextModal suggestion={suggestion} setSuggestion={setSuggestion}>{t11}</RegenerateAITextModal>;
    $[26] = setSuggestion;
    $[27] = suggestion;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== dismissSuggestion || $[31] !== setSuggestion || $[32] !== suggestion.id) {
    t13 = () => {
      setSuggestion?.(undefined);
      dismissSuggestion(suggestion.id);
    };
    $[30] = dismissSuggestion;
    $[31] = setSuggestion;
    $[32] = suggestion.id;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <HiX className="h-4 w-4" />;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t13) {
    t15 = <Tooltip content="Dismiss suggestion"><Button type="button" variant="ghost" size="icon-sm" onClick={t13} className="h-6 w-6 shrink-0 p-1 text-accent-foreground hover:bg-accent-foreground/10 hover:text-accent-foreground">{t14}</Button></Tooltip>;
    $[35] = t13;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t12 || $[38] !== t15) {
    t16 = <div className="flex shrink-0 items-center gap-0.5">{t12}{t15}</div>;
    $[37] = t12;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t16 || $[41] !== t6) {
    t17 = <div className="flex items-center justify-between gap-2">{t6}{t16}</div>;
    $[40] = t16;
    $[41] = t6;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const t18 = isTool ? (suggestion as ToolCallSummary).toolCallDescription : (suggestion as SuggestedMessage).content;
  let t19;
  if ($[43] !== t18) {
    t19 = <div className="whitespace-pre-wrap break-words text-body text-accent-foreground/90">{t18}</div>;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== onAction || $[46] !== suggestion) {
    t20 = () => onAction(suggestion);
    $[45] = onAction;
    $[46] = suggestion;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  const t21 = isTool ? (suggestion as ToolCallSummary).hasBeenCalled ? "Executed" : "Execute" : "Use";
  let t22;
  if ($[48] !== isDisabled || $[49] !== t20 || $[50] !== t21) {
    t22 = <div className="flex justify-end"><Button variant="secondary" onClick={t20} disabled={isDisabled}>{t21}</Button></div>;
    $[48] = isDisabled;
    $[49] = t20;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t17 || $[53] !== t19 || $[54] !== t22) {
    t23 = <div className="flex flex-col gap-2">{t17}{t19}{t22}</div>;
    $[52] = t17;
    $[53] = t19;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t2 || $[57] !== t23) {
    t24 = <AnimatePresence><motion.div className="ml-auto w-full max-w-[85%] rounded-lg border border-accent-foreground/20 bg-accent p-3" animate={t2} transition={t3}>{t23}</motion.div></AnimatePresence>;
    $[56] = t2;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  return t24;
}